import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { Box } from "@mui/material";
import "../BrandMetadataReview.css";
import BrandReviewInstructions from "../UI/BrandReviewInstructions";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";
import { GridColDef } from "@mui/x-data-grid-premium";
import { renderNumberCell } from "utils/dataGrid";

interface BrandMetadataReviewProps {
  chain_id: string;
}

const columns_def: GridColDef[] = [
  {
    field: "brand_name",
    headerName: "Brand Name",
    description: "The name of the brand.",
    type: "string",
    width: 200,
  },
  {
    field: "geo_status",
    headerName: "Geo Status",
    description:
      "A status describing the geo association, including metadata and country code relationships.",
    type: "string",
    width: 300,
  },
  {
    field: "country_code",
    headerName: "Country Code",
    description:
      "The country code associated with the geo. This is `None` if no association exists.",
    type: "string",
    width: 150,
  },
  {
    field: "geo",
    headerName: "Geo",
    description: "The geographic identifier.",
    type: "string",
    width: 200,
  },
  {
    field: "country_name",
    headerName: "Country Name",
    description:
      "The name of the country associated with the country code. This is `None` if unavailable.",
    type: "string",
    width: 200,
  },
];

const ReviewBrandGeos: React.FC<BrandMetadataReviewProps> = ({ chain_id }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>([]);

  const { showError } = useError();
  const { apiRequest } = useApi();

  useEffect(() => {
    if (chain_id) {
      setLoading(true);
      const params = new URLSearchParams({
        chain_id: chain_id,
      });

      const fetchBrandGeos = async () => {
        try {
          const response = await apiRequest<any>(
            `/brand_metadata/business_geos?${params.toString()}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch brand geos to review for chain_id ${chain_id}`
          );
        } finally {
          setLoading(false);
        }
      };
      fetchBrandGeos();
    } else {
      setData([]);
    }
  }, [chain_id]);

  const user_instructions = {
    section_id: "brand-geos",
    instruction_title: "Review List of Geos",
    purpose:
      "The `geos` attribute in the metadata contains OpenStreetMap (OSM) relation IDs, which are unique identifiers representing geographic regions. These IDs are critical for standardizing and codifying geographic information, enabling accurate mapping and data integration. Using codified `geos` ensures consistent identification of countries, avoids ambiguity in geographic boundaries, and facilitates alignment with external geographic datasets.",
    goal: "Ensure every country listed in the `estimated_counts_by_country` attribute is accurately represented in the metadata with a corresponding `geo` relation ID.",
    task_description:
      "Review the countries listed in the `estimated_counts_by_country` attribute and verify that each has an associated OSM relation ID in the `geos` attribute. Validate the accuracy of the existing `geos` and add missing entries where applicable.",
    task_steps: [
      {
        title: "Review Current Geo Data",
        instructions: [
          "Use the table below to check the existing values in the `geos` attribute in the metadata to ensure it includes an OSM relation ID for each country listed in the `estimated_counts_by_country` attribute.",
        ],
      },
      {
        title: "Identify Missing or Incorrect Geos",
        instructions: [
          "Identify any countries from the `estimated_counts_by_country` attribute that are missing a corresponding `geo` relation ID in the metadata.",
          "Verify the accuracy of existing `geo` relation IDs that do not have a corresponding country code by checking whether the associated country is a valid country of operation. If it is, go to the section on country review for instructions on adding a country code.",
        ],
      },
      {
        title: "Update Metadata",
        instructions: [
          {
            text: "For validated geos, update the `geos` attribute in `Kuebiko`:",
            sublist: [
              "Navigate to the brand in `Kuebiko`.",
              "Select `Brand Edit`.",
              "Update the `geos` attribute to include the missing. See section on country review for instructions on adding a country code if a required country code is missing.",
            ],
          },
        ],
      },
    ],
  };

  return (
    <Box className="metadata-review-container">
      <BrandReviewInstructions {...user_instructions} />
      <Box sx={{ mt: 5 }}>
        <GaneshDataGrid
          gridId="reviewBrandMetadataGeosGrid"
          columns={columns_def}
          data={data}
          loading={loading}
          headerFilters={data.length > 0 ? true : false}
          customHeight={data.length > 0 ? "750px" : "300px"}
        />
      </Box>
    </Box>
  );
};

export default ReviewBrandGeos;
